// Global toast registry — minimal pub/sub backed by a React store hook.
import { useEffect, useState, useCallback } from 'react';

const listeners = new Set();
let nextId = 1;
const toasts = [];

function notify() {
  for (const cb of listeners) cb(toasts.slice());
}

function pushToast(toast) {
  const id = nextId++;
  // Errors persist until dismissed unless the caller opts into a duration;
  // everything else auto-dismisses after 4s (tester-approved UAT change).
  const defaultDurationMs = toast?.tone === 'error' ? 0 : 4000;
  const t = {
    id,
    tone: 'info',
    durationMs: defaultDurationMs,
    ...toast,
  };
  toasts.push(t);
  notify();
  if (t.durationMs > 0) {
    setTimeout(() => dismissToast(id), t.durationMs);
  }
  return id;
}

function dismissToast(id) {
  const idx = toasts.findIndex((t) => t.id === id);
  if (idx >= 0) {
    toasts.splice(idx, 1);
    notify();
  }
}

export const toast = {
  show: (input) => pushToast(typeof input === 'string' ? { body: input } : input),
  success: (input) =>
    pushToast(
      typeof input === 'string' ? { body: input, tone: 'success' } : { ...input, tone: 'success' },
    ),
  warn: (input) =>
    pushToast(
      typeof input === 'string' ? { body: input, tone: 'warn' } : { ...input, tone: 'warn' },
    ),
  error: (input) =>
    pushToast(
      typeof input === 'string' ? { body: input, tone: 'error' } : { ...input, tone: 'error' },
    ),
  dismiss: dismissToast,
};

export function useToastList() {
  const [list, setList] = useState(toasts.slice());
  useEffect(() => {
    listeners.add(setList);
    setList(toasts.slice());
    return () => listeners.delete(setList);
  }, []);
  return { toasts: list, dismiss: useCallback(dismissToast, []) };
}
